// src/components/security-toolbox-ui/StbSingleSelectWithSearch.tsx

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { HbrIcon, HbrInput } from '@harbor/elements-react-standalone';
import { magnifyingGlass, statusInfoSquare } from '@harbor/elements/icons';

export interface StbSingleSelectItem {
  id: string;
  name: string;
  group?: string;
}

interface StbSingleSelectWithSearchProps {
  value: StbSingleSelectItem | null;
  /** Items to display (e.g., search results from parent). */
  items: StbSingleSelectItem[];
  /** Called whenever the user changes the search term. Parent decides when/how to query. */
  onSearchTermChange?: (term: string) => void;
  /** Called when user selects an item or clears. */
  onChange: (item: StbSingleSelectItem | null) => void;
  groupBy?: 'group' | string;
  searchPlaceholder?: string;
  className?: string;
  inline?: boolean;
}

const StbSingleSelectWithSearch: React.FC<StbSingleSelectWithSearchProps> = ({
  value,
  items,
  onSearchTermChange,
  onChange,
  groupBy = 'group',
  searchPlaceholder = 'Search items',
  className,
  inline = false,
}) => {
  const [search, setSearch] = useState<string>('');
  const [openDropdown, setOpenDropdown] = useState<boolean>(false);
  const wrapper = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const onDocMouseDown = (e: MouseEvent) => {
      if (!wrapper.current) return;
      if (!wrapper.current.contains(e.target as Node)) {
        setOpenDropdown(false);
      }
    };
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, []);

  const grouped = useMemo(() => {
    if (!items.length) return {};
    return items.reduce((acc, item) => {
      const key = (item as any)[groupBy] || 'Results';
      if (!acc[key]) acc[key] = [];
      acc[key].push(item);
      return acc;
    }, {} as Record<string, StbSingleSelectItem[]>);
  }, [items, groupBy]);

  const sortedGroups = useMemo(
    () =>
      Object.entries(grouped)
        .map(([groupLabel, groupItems]) => ({ groupLabel, items: groupItems }))
        .sort((a, b) => a.groupLabel.localeCompare(b.groupLabel)),
    [grouped],
  );

  const handleSearchInput = (event: CustomEvent<any>) => {
    const val = (event.target as any)?.value ?? '';
    setSearch(val);
    if (onSearchTermChange) {
      onSearchTermChange(val);
    }
  };

  const handleItemClick = (item: StbSingleSelectItem) => {
    onChange(item);
    setOpenDropdown(false);
  };

  const handleClear = () => {
    onChange(null);
  };

  const selectedLabel = value?.name || '';

  return (
    <div ref={wrapper} className={`relative flex flex-col ${className ?? ''}`}>
      {/* Selected value */}
      <div
        className="hbr-css__single-select-display"
        style={{
          border: '1px solid var(--hbr-color-border-default, #ccc)',
          borderRadius: '4px',
          padding: '4px 8px',
          minHeight: '32px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          cursor: 'pointer',
        }}
        onClick={() => setOpenDropdown((o) => !o)}
      >
        <span>{selectedLabel || 'Select an account'}</span>
        {value && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              handleClear();
            }}
            style={{
              border: 'none',
              background: 'transparent',
              cursor: 'pointer',
              padding: 0,
              marginLeft: '8px',
            }}
          >
            ×
          </button>
        )}
      </div>

      {openDropdown && (
        <div
          className={
            inline
              ? 'mt-2 z-50 bg-white shadow-lg w-full'
              : 'absolute left-0 mt-2 z-50 bg-white shadow-lg w-full'
          }
        >
          {/* Search */}
          <div style={{ padding: '8px' }}>
            <HbrInput
              placeholder={searchPlaceholder}
              value={search}
              clearable={true}
              size="small"
              type="search"
              onHbr-input={handleSearchInput as any}
              className="ml-0 mb-2"
              onHbr-focus={() => setOpenDropdown(true)}
            >
              <HbrIcon
                slot="prefix"
                name="magnifying-glass"
                asset={magnifyingGlass}
                size="16px"
                staticColors={true}
              />
            </HbrInput>
          </div>

          {/* Results */}
          <div
            className="border border-gray-200 divide-y divide-gray-200 w-full rounded-b-md overflow-auto max-h-72"
            style={{ borderTop: 'none' }}
          >
            {sortedGroups.length === 0 ? (
              <div className="bg-blue-100 border border-blue-200 text-blue-800 w-full px-4 py-3 rounded-md mb-4 flex items-center gap-2">
                <HbrIcon asset={statusInfoSquare} sentiment="info" />
                No items found
              </div>
            ) : (
              sortedGroups.map(({ groupLabel, items }) => (
                <div key={groupLabel}>
                  <div
                    style={{
                      padding: '4px 8px',
                      background: 'var(--hbr-color-bg-subtle, #f7f7f7)',
                      fontSize: '12px',
                      fontWeight: 600,
                    }}
                  >
                    {groupLabel}
                  </div>
                  {items.map((item) => (
                    <div
                      key={item.id}
                      onClick={() => handleItemClick(item)}
                      style={{
                        padding: '4px 8px',
                        cursor: 'pointer',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {item.name} ({item.id})
                    </div>
                  ))}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default StbSingleSelectWithSearch;